import { useMemo, useState, useEffect } from "react";
import { Trophy, Calendar, Radio, Users } from "lucide-react";
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerDescription,
} from "@/components/ui/drawer";
import { api } from "@/lib/api";

const KNOCKOUT_STAGES = [
    { key: "r64", label: "R64" },
    { key: "r32", label: "R32" },
    { key: "r16", label: "R16" },
    { key: "qf", label: "QF" },
    { key: "sf", label: "SF" },
    { key: "final", label: "Final" },
    { key: "champion", label: "Champion" },
];

const STAGE_RANK = { group: 0, r64: 1, r32: 2, r16: 3, qf: 4, sf: 5, final: 6, champion: 7 };

// iter40 — Map a scheduled-matches `round_label` (or ESPN's "Postseason"
// label + date window) to one of our internal stage keys. Used so the
// bracket can render upcoming/unplayed games sourced from the schedule
// API instead of a hardcoded skeleton.
// Default fallback used until `GET /api/ko-windows/{template}` resolves
// (and forever in tests / SSR snapshots). Must stay in sync with backend
// `espn_sync.KO_WINDOWS_BY_TEMPLATE`.
const _KO_WINDOWS_DEFAULT_BY_TEMPLATE = {
    world_cup: [
        { stage: "r32",   start: "2026-06-28", end: "2026-07-03" },
        { stage: "r16",   start: "2026-07-04", end: "2026-07-07" },
        { stage: "qf",    start: "2026-07-09", end: "2026-07-11" },
        { stage: "sf",    start: "2026-07-14", end: "2026-07-15" },
        { stage: "final", start: "2026-07-19", end: "2026-07-19" },
    ],
    march_madness: [
        { stage: "r64",   start: "2026-03-19", end: "2026-03-20" },
        { stage: "r32",   start: "2026-03-21", end: "2026-03-22" },
        { stage: "r16",   start: "2026-03-26", end: "2026-03-27" },
        { stage: "qf",    start: "2026-03-28", end: "2026-03-29" },
        { stage: "sf",    start: "2026-04-04", end: "2026-04-04" },
        { stage: "final", start: "2026-04-06", end: "2026-04-06" },
    ],
};
_KO_WINDOWS_DEFAULT_BY_TEMPLATE.world_cup_knockout = _KO_WINDOWS_DEFAULT_BY_TEMPLATE.world_cup;
// iter84.57.7 — Alias the API-Football template keys onto the existing
// World-Cup KO date windows so the bracket-window demotion guard fires
// for `world_cup_api` (group_knockout) and `world_cup_ko_api`
// (knockout_only) tournaments too. Without these aliases, the guard
// was a no-op for API-Football tournaments and the renderer relied
// entirely on the upstream round_label, which is the bug we're also
// fixing in _scheduleEntryStage below.
_KO_WINDOWS_DEFAULT_BY_TEMPLATE.world_cup_api = _KO_WINDOWS_DEFAULT_BY_TEMPLATE.world_cup;
_KO_WINDOWS_DEFAULT_BY_TEMPLATE.world_cup_ko_api = _KO_WINDOWS_DEFAULT_BY_TEMPLATE.world_cup;
const _TEMPLATES_WITH_KO_WINDOWS = new Set(Object.keys(_KO_WINDOWS_DEFAULT_BY_TEMPLATE));

function _scheduleEntryStage(entry, template, windows) {
    const lbl = (entry.round_label || "").toUpperCase();
    // iter40r16 — Trust EXPLICIT KO round labels. For ESPN's generic
    // "POSTSEASON" label, bucket by date window with a 14-hour UTC buffer
    // so we don't false-bucket a Jun 27 19:00 CDT kickoff (stamped
    // 2026-06-28T02:00Z) into R32 just because its UTC-day matches the
    // window's start day. Removed the older naive date-matching fallback
    // for unlabeled (round_label=null) entries — those now correctly fall
    // off the bracket; the schedule view still surfaces them in date order.
    //
    // iter84.57.7 — API-Football emits verbose round labels like
    //   "Round of 32" / "Round of 16" / "Quarter-finals" / "Semi-finals"
    // which the original short-form table didn't recognise, so every
    // API-Football KO fixture was being silently dropped from the
    // bracket (the WC-Regular bracket rendered fully TBD as a result).
    // The mapping below mirrors the backend's `round_to_stage`
    // (api_football.py:242) — keep them in sync if upstream adds new
    // round shapes.
    const labelToStage = {
        // Short forms (ESPN, GotSport)
        "R64": "r64", "R32": "r32", "R16": "r16", "QF": "qf", "SF": "sf", "FINAL": "final",
        // Verbose forms (API-Football wire format)
        "ROUND OF 64": "r64",
        "ROUND OF 32": "r32",
        "ROUND OF 16": "r16",
        "QUARTER-FINALS": "qf",
        "QUARTERFINALS": "qf",
        "QUARTER FINALS": "qf",
        "SEMI-FINALS": "sf",
        "SEMIFINALS": "sf",
        "SEMI FINALS": "sf",
        "3RD PLACE FINAL": "final",
        "THIRD PLACE FINAL": "final",
    };
    if (lbl in labelToStage) {
        const expected = labelToStage[lbl];
        const win = windows.find(w => w.stage === expected);
        const day = (entry.scheduled_at || "").slice(0, 10);
        if (day && win && day < win.start) return null;  // demote — group game leaking through
        return expected;
    }
    if (lbl !== "POSTSEASON") return null;
    // POSTSEASON path: only bucket if the kickoff is INSIDE one of the
    // windows AND the UTC hour-of-day is >= 14 (rejects Jun 27 evening local
    // / Jun 28 early-morning UTC kickoffs that aren't actually R32 games).
    if (!entry.scheduled_at) return null;
    const dt = new Date(entry.scheduled_at);
    if (isNaN(dt.getTime())) return null;
    const day = entry.scheduled_at.slice(0, 10);
    for (const w of windows) {
        if (day >= w.start && day <= w.end) {
            // Same-day-as-window-start guard: a kickoff < 14:00 UTC on the
            // window's first day is almost certainly the prior local day.
            if (day === w.start && dt.getUTCHours() < 14) return null;
            return w.stage;
        }
    }
    return null;
}

function formatKickoff(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
        });
    } catch { return null; }
}

/**
 * Interactive bracket: horizontal stage columns, click any match to open
 * a hover-card (desktop) or drawer (mobile) with full match detail +
 * which participants drafted the involved teams. Your own teams are
 * highlighted in cyber-yellow for fast self-spotting.
 */
export default function BracketView({ tournament, matches, myTeamIds = [], participants = [] }) {
    const [drawerMatch, setDrawerMatch] = useState(null);

    // iter40 — Fetch centralized KO date-windows from backend for this
    // tournament's template. Falls back to the hardcoded default (derived
    // from template via useMemo so first paint isn't blocked / SSR works).
    // Templates without KO windows (round_robin, olympics, custom) keep
    // windows = [] which is harmless.
    const defaultKoWindows = useMemo(
        () => _KO_WINDOWS_DEFAULT_BY_TEMPLATE[tournament?.template] || [],
        [tournament?.template]
    );
    const [fetchedKoWindows, setFetchedKoWindows] = useState(null);
    const koWindows = fetchedKoWindows || defaultKoWindows;
    useEffect(() => {
        let cancelled = false;
        const tpl = tournament?.template;
        if (!tpl || !_TEMPLATES_WITH_KO_WINDOWS.has(tpl)) return undefined;
        api.getKoWindows(tpl)
            .then(d => { if (!cancelled && Array.isArray(d?.windows) && d.windows.length) setFetchedKoWindows(d.windows); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [tournament?.template]);

    const knockoutStages = useMemo(() => {
        const tournamentStageKeys = (tournament?.stages || []).map(s => s.key);
        return KNOCKOUT_STAGES.filter(s => tournamentStageKeys.includes(s.key));
    }, [tournament]);

    const teamsAtStage = useMemo(() => {
        const map = {};
        knockoutStages.forEach(s => {
            map[s.key] = (tournament?.teams || []).filter(
                t => STAGE_RANK[t.stage_reached] >= STAGE_RANK[s.key]
            );
        });
        return map;
    }, [tournament, knockoutStages]);

    const matchesByStage = useMemo(() => {
        const map = {};
        (matches || []).forEach(m => {
            const stage = m.stage;
            // iter84.58 — Removed the date-window demoter that auto-rewrote
            // `r32` etc. back to "group" when the kickoff fell before the
            // round's published window. The server-side per-match Round
            // Override is now the source of truth for ambiguous rows.
            if (!map[stage]) map[stage] = [];
            map[stage].push(m);
        });
        return map;
    }, [matches]);

    const teamById = useMemo(
        () => Object.fromEntries((tournament?.teams || []).map(t => [t.id, t])),
        [tournament]
    );
    // Lookup teams by bracket_slot was removed (iter86.83 cleanup — computed
    // but never consumed since the schedule-entry render path took over).

    // iter40 — Fetch the schedule once and bucket entries by stage. The
    // bracket renders upcoming/unplayed games from these entries so labels
    // come straight from ESPN/GotSport once published, with the synthesised
    // backend skeleton ("Group A Winner" / "Winner R32 Game 3") as a fallback
    // before ESPN publishes the fixtures.
    const [scheduleEntries, setScheduleEntries] = useState([]);
    const [scheduleFetchedAt, setScheduleFetchedAt] = useState(null);
    useEffect(() => {
        let cancelled = false;
        if (!tournament?.id) return undefined;
        api.getSchedule(tournament.id, { from: tournament.start_date, to: tournament.end_date })
            .then(d => {
                if (cancelled) return;
                setScheduleEntries(d.entries || []);
                setScheduleFetchedAt(new Date());
            })
            .catch(() => { if (!cancelled) setScheduleEntries([]); });
        return () => { cancelled = true; };
    }, [tournament?.id, tournament?.start_date, tournament?.end_date]);

    // iter84.58 — Bucket schedule entries strictly by their explicit
    // `round_label`. The backend no longer injects "TBD vs TBD"
    // skeleton entries; we also filter out any leftover skeleton rows
    // defensively (`is_skeleton=true`) so only real, operator-pinned
    // or ESPN-imported fixtures show up in the bracket.
    const scheduledByStage = useMemo(() => {
        const buckets = { r64: [], r32: [], r16: [], qf: [], sf: [], final: [] };
        const template = tournament?.template;
        for (const e of scheduleEntries) {
            if (e?.is_skeleton) continue;
            const stage = _scheduleEntryStage(e, template, koWindows);
            if (!stage || !(stage in buckets)) continue;
            buckets[stage].push(e);
        }
        // Sort each bucket by scheduled_at so the rendering order matches
        // chronological game order (R32 Game 1 first, Game 16 last).
        for (const k of Object.keys(buckets)) {
            buckets[k].sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""));
        }
        return buckets;
    }, [scheduleEntries, tournament?.template, koWindows]);

    const myIdSet = useMemo(() => new Set(myTeamIds), [myTeamIds]);

    // Build a lookup: team_id → array of participant usernames who drafted it
    const ownersByTeamId = useMemo(() => {
        const map = {};
        (participants || []).forEach(p => {
            (p.assigned_team_ids || []).forEach(tid => {
                if (!map[tid]) map[tid] = [];
                map[tid].push(p.username);
            });
            // Knockout-bonus champion picks count as ownership too
            if (p.knockout_pick_team_id) {
                if (!map[p.knockout_pick_team_id]) map[p.knockout_pick_team_id] = [];
                if (!map[p.knockout_pick_team_id].includes(`${p.username} (champ pick)`)) {
                    map[p.knockout_pick_team_id].push(`${p.username} (champ pick)`);
                }
            }
        });
        return map;
    }, [participants]);

    if (!tournament || knockoutStages.length === 0) {
        return (
            <div className="brutal-card p-6 text-center text-zinc-500">
                <Trophy className="w-10 h-10 mx-auto text-zinc-700 mb-2" />
                <p>No knockout bracket configured for this tournament.</p>
            </div>
        );
    }

    return (
        <div data-testid="bracket-view">
            {/* iter87.25 — Outer card + "Bracket" header removed;
                Takeover shell already carries icon + title. Kept the
                per-round "Updated at" timestamp right-aligned. */}
            <div className="flex items-start justify-end gap-3 mb-1">
                {scheduleFetchedAt && (
                    <p
                        className="text-[10px] text-zinc-500 mt-1 shrink-0"
                        data-testid="bracket-last-updated"
                        title={scheduleFetchedAt.toLocaleString()}
                    >
                        Updated {scheduleFetchedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                    </p>
                )}
            </div>
            <p className="text-xs text-zinc-500 mb-4">
                Tap a match for kickoff time, score, and picks. Your drafted teams glow yellow.
                {tournament?.count_third_place_playoff && (
                    <>
                        {" · "}
                        <a
                            href="#bracket-bronze-match"
                            data-testid="bracket-bronze-jump-link"
                            className="text-blaze font-bold hover:underline"
                            onClick={(e) => {
                                e.preventDefault();
                                document.querySelector('[data-testid="bracket-bronze-match"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
                            }}
                        >🥉 Bronze match enabled — jump to it ↓</a>
                    </>
                )}
            </p>

            <div className="overflow-x-auto pb-2">
                <div className="flex gap-4 min-w-min">
                    {knockoutStages.map(stage => {
                        const realMatches = matchesByStage[stage.key] || [];
                        const teamsHere = teamsAtStage[stage.key] || [];
                        // iter84.58 — Render played + upcoming side-by-side.
                        //
                        // Pre-fix: `showScheduledSkeleton` only fired when
                        // `realMatches.length === 0`, so as soon as ONE R32
                        // game finished, the other 15 scheduled-but-unplayed
                        // R32 fixtures disappeared from the bracket — a bug
                        // for any tournament whose round spans multiple
                        // days (every real WC + MM bracket). Now we render
                        // both, deduped by `match_id`: a scheduled row that
                        // already links to a played match is skipped (it's
                        // shown as a played MatchCard above).
                        //
                        // iter84.60.4.6 — Under the iter84.60 model the
                        // schedule endpoint reads from `fixtures`, where
                        // each entry's `match_id` is the FIXTURE id (not
                        // the legacy `matches.id`). The old `match_id`-in-
                        // `playedMatchIds` check therefore never matched,
                        // and every completed R32 game rendered TWICE —
                        // once as a played MatchCard (from `matches`) and
                        // once as a ScheduledMatchCard "MATCH N ROUND OF 32"
                        // stub (from `fixtures.status="post"`). Fix: also
                        // drop entries flagged `played=true` — the played
                        // MatchCard above already represents them.
                        //
                        // `champion` stage stays cards-free (it's the
                        // overall winner cap, not a bracket slot).
                        const playedMatchIds = new Set(
                            realMatches.map(m => m.id).filter(Boolean),
                        );
                        const scheduledForStage = (
                            scheduledByStage[stage.key] || []
                        ).filter(e => {
                            if (e.played) return false;
                            if (e.match_id && playedMatchIds.has(e.match_id)) return false;
                            return true;
                        });
                        const showScheduledSkeleton =
                            stage.key !== "champion"
                            && scheduledForStage.length > 0;
                        // Plain TBD count fallback when neither played matches nor
                        // scheduled entries exist (e.g. ESPN data missing entirely).
                        const TBD_BY_STAGE = { r32: 16, r16: 8, qf: 4, sf: 2, final: 1 };
                        const tbdCount = TBD_BY_STAGE[stage.key] || 0;
                        const showTbdSkeleton =
                            stage.key !== "champion" && realMatches.length === 0
                            && scheduledForStage.length === 0 && teamsHere.length === 0
                            && tbdCount > 0;
                        return (
                        <div key={stage.key} className="flex-shrink-0 w-52">
                            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 text-center font-bold">
                                {stage.label}
                            </div>
                            <div className="space-y-2">
                                {stage.key !== "champion" && realMatches.map(m => {
                                    const teamA = teamById[m.team_a_id];
                                    const teamB = teamById[m.team_b_id];
                                    if (!teamA || !teamB) return null;
                                    return (
                                        <MatchCard
                                            key={m.id}
                                            match={m}
                                            teamA={teamA}
                                            teamB={teamB}
                                            myIdSet={myIdSet}
                                            ownersByTeamId={ownersByTeamId}
                                            onTap={(mm) => setDrawerMatch({ match: mm, teamA, teamB })}
                                        />
                                    );
                                })}
                                {/* iter40 — Schedule-sourced upcoming-match cards.
                                    Labels match ESPN's published fixtures or the
                                    backend placeholder until ESPN publishes. */}
                                {showScheduledSkeleton && scheduledForStage.map((entry, idx) => (
                                    <ScheduledMatchCard
                                        key={entry.id || `sched-${stage.key}-${idx}`}
                                        gameNum={idx + 1}
                                        entry={entry}
                                        teamById={teamById}
                                        myIdSet={myIdSet}
                                    />
                                ))}
                                {/* Plain TBD placeholders only when the schedule
                                    has nothing for this stage. */}
                                {showTbdSkeleton && Array.from({ length: tbdCount }, (_, i) => (
                                    <div
                                        key={`tbd-${stage.key}-${i}`}
                                        data-testid={`bracket-tbd-${stage.key}-${i + 1}`}
                                        className="brutal-card-light p-2 text-xs space-y-1 border-dashed opacity-60"
                                    >
                                        <div className="flex items-center justify-between text-zinc-500">
                                            <span className="text-[10px] uppercase tracking-widest">Match {i + 1}</span>
                                        </div>
                                        <div className="text-zinc-600">TBD</div>
                                        <div className="text-zinc-600">TBD</div>
                                    </div>
                                ))}
                                {stage.key !== "champion" && !showScheduledSkeleton && realMatches.length === 0 &&
                                    teamsHere.map(t => {
                                        const isMine = myIdSet.has(t.id);
                                        return (
                                            <div
                                                key={t.id}
                                                data-testid={`bracket-team-${t.id}`}
                                                className={`brutal-card-light p-2 text-xs flex items-center gap-2 ${isMine ? "border-cyber bg-cyber/10" : ""}`}
                                            >
                                                <span>{t.flag || "🏳️"}</span>
                                                <span className={`truncate font-semibold ${isMine ? "text-cyber" : ""}`}>{t.name}</span>
                                            </div>
                                        );
                                    })
                                }
                                {stage.key === "champion" && (teamsAtStage["champion"] || []).map(t => {
                                    const isMine = myIdSet.has(t.id);
                                    return (
                                        <div
                                            key={t.id}
                                            data-testid={`bracket-champion-${t.id}`}
                                            className={`brutal-card p-3 text-center shadow-brutalYellow ${isMine ? "bg-cyber/30 border-cyber" : "bg-cyber/20 border-cyber"}`}
                                        >
                                            <Trophy className="w-5 h-5 mx-auto text-cyber mb-1" />
                                            <div className="text-lg">{t.flag || "🏳️"}</div>
                                            <div className={`text-xs font-bold ${isMine ? "text-cyber" : ""}`}>{t.name}</div>
                                            {isMine && <div className="text-[9px] uppercase tracking-widest text-cyber mt-1">your team</div>}
                                        </div>
                                    );
                                })}
                                {stage.key === "champion" && (teamsAtStage["champion"] || []).length === 0 && (tournament?.template === "world_cup" || tournament?.template === "world_cup_knockout") && (
                                    <div
                                        data-testid="bracket-champion-tbd"
                                        className="brutal-card p-3 text-center border-dashed border-cyber/40 opacity-60"
                                    >
                                        <Trophy className="w-5 h-5 mx-auto text-zinc-600 mb-1" />
                                        <div className="text-lg">🏆</div>
                                        <div className="text-xs font-bold text-zinc-500">TBD</div>
                                    </div>
                                )}
                            </div>
                        </div>
                        );
                    })}
                </div>
            </div>

            {/* iter35f — 3rd-place playoff card. Only shown when the tournament
                opted-in via count_third_place_playoff. Sits below the bracket
                grid so the bronze match is visible as a real bracket fixture. */}
            {tournament?.count_third_place_playoff && (() => {
                const sfTeams = (tournament?.teams || []).filter(
                    t => t.stage_reached === "sf"
                );
                const bronzeWinnerIds = new Set(tournament?.bronze_winners || []);
                return (
                    <div
                        data-testid="bracket-bronze-match"
                        className="mt-4 brutal-card-light p-4 border-blaze/40"
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">🥉</span>
                            <h4 className="font-display font-extrabold text-sm uppercase tracking-tight">Bronze Medal Match</h4>
                            {sfTeams.length === 2 && bronzeWinnerIds.size === 0 && (
                                <span className="pill bg-zinc-700 border-zinc-600 text-zinc-300 text-[9px] py-0 px-1.5">SCHEDULED</span>
                            )}
                            {bronzeWinnerIds.size > 0 && (
                                <span className="pill bg-blaze/20 border-blaze text-blaze text-[9px] py-0 px-1.5">SETTLED</span>
                            )}
                        </div>
                        {sfTeams.length === 0 ? (
                            <p className="text-xs text-zinc-500 italic">
                                The bronze match populates once both semifinal losers are known.
                            </p>
                        ) : sfTeams.length === 1 ? (
                            <p className="text-xs text-zinc-500 italic">
                                Waiting for the second semifinal loser…
                            </p>
                        ) : (
                            <div className="grid grid-cols-2 gap-2">
                                {sfTeams.map(t => {
                                    const isWinner = bronzeWinnerIds.has(t.id);
                                    const isMine = myTeamIds.includes(t.id);
                                    return (
                                        <div
                                            key={t.id}
                                            data-testid={`bronze-match-team-${t.id}`}
                                            className={`p-2 rounded-xl border ${
                                                isWinner ? "border-blaze/60 bg-blaze/10" :
                                                isMine ? "border-cyber/50" :
                                                "border-white/[0.08]"
                                            }`}
                                        >
                                            <div className="flex items-center gap-1.5">
                                                <span>{t.flag || "🏳️"}</span>
                                                <span className={`text-xs truncate ${isWinner ? "font-bold" : ""}`}>{t.name}</span>
                                                {isWinner && <span className="text-[9px] font-bold text-blaze">🥉</span>}
                                                {isMine && <span className="pill bg-cyber text-black text-[8px] py-0 px-1">you</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <p className="text-[10px] text-zinc-500 italic mt-2">
                            Winner earns the bronze bonus ({tournament?.scoring?.bronze_points ?? 15} pts) on top of their semifinal points.
                        </p>
                    </div>
                );
            })()}

            {/* Mobile drawer (hover-card already handles desktop) */}
            <Drawer open={!!drawerMatch} onOpenChange={(open) => !open && setDrawerMatch(null)}>
                <DrawerContent className="bg-ink border-t-4 border-cyber text-white" data-testid="bracket-drawer-content">
                    {drawerMatch && (
                        <>
                            <DrawerHeader>
                                <DrawerTitle className="font-display font-extrabold text-xl uppercase">
                                    {drawerMatch.teamA.name} vs {drawerMatch.teamB.name}
                                </DrawerTitle>
                                <DrawerDescription className="text-zinc-400 text-xs uppercase tracking-widest">
                                    {drawerMatch.match.stage}{drawerMatch.match.group ? ` · Group ${drawerMatch.match.group}` : ""}
                                </DrawerDescription>
                            </DrawerHeader>
                            <div className="p-5 pt-0 space-y-4 pb-8">
                                <MatchDetailBody
                                    match={drawerMatch.match}
                                    teamA={drawerMatch.teamA}
                                    teamB={drawerMatch.teamB}
                                    myIdSet={myIdSet}
                                    ownersByTeamId={ownersByTeamId}
                                />
                            </div>
                        </>
                    )}
                </DrawerContent>
            </Drawer>
        </div>
    );
}

// Skeleton (pre-tournament) match card: shows the matchup before games begin.
// Renders bracket-slot labels (e.g., "1A — Group A Winner") and uses dashed
// iter40 — Renders a scheduled (not yet played) bracket slot using the
// labels + team_ids from the backend schedule API. When ESPN/GotSport
// have published the fixture, this shows the real team names (and we
// highlight cyber if either team is one of yours). Otherwise it falls
// back to the placeholder labels ("Group A Winner", "Winner R32 Game 3").
function ScheduledMatchCard({ gameNum, entry, teamById, myIdSet }) {
    const teamA = entry.home_team_id ? teamById[entry.home_team_id] : null;
    const teamB = entry.away_team_id ? teamById[entry.away_team_id] : null;
    const aMine = teamA && myIdSet.has(teamA.id);
    const bMine = teamB && myIdSet.has(teamB.id);
    const anyMine = aMine || bMine;
    const stageLbl = (entry.round_label || "").toUpperCase() || "TBD";
    return (
        <div
            data-testid={`bracket-scheduled-${stageLbl.toLowerCase()}-${gameNum}`}
            className={`brutal-card-light p-2 text-xs space-y-1 border-dashed ${anyMine ? "border-cyber/60 bg-cyber/5" : "opacity-80"}`}
        >
            <div className="flex items-center justify-between text-zinc-500 text-[10px] uppercase tracking-widest">
                <span>Match {gameNum}</span>
                <span>{stageLbl}</span>
            </div>
            <ScheduledTeamLine team={teamA} label={entry.home_label} isMine={aMine} />
            <ScheduledTeamLine team={teamB} label={entry.away_label} isMine={bMine} />
        </div>
    );
}

function ScheduledTeamLine({ team, label, isMine }) {
    // iter84.57.8 — When the team_id hasn't resolved yet (API-Football
    // bracket not drawn / FIFA pairings pending), render the upstream
    // placeholder label ("Winner Group A", "Best 3rd Place vs Group L")
    // in dotted-italic + muted-gray so it's visually distinct from
    // resolved matchups. This keeps the bracket structure complete
    // even before real-world pairings are finalised.
    const isPlaceholder = !team;
    const displayName = team?.name || label || "TBD";
    return (
        <div className={`flex items-center gap-2 ${isMine ? "text-cyber font-bold" : isPlaceholder ? "text-zinc-500" : "text-zinc-400"}`}>
            <span className="opacity-60">{team?.flag || "🏳️"}</span>
            <span
                className={`truncate flex-1 ${isPlaceholder ? "italic decoration-dotted underline decoration-zinc-600 underline-offset-2" : ""}`}
                title={isPlaceholder ? "Pairing not yet drawn upstream" : undefined}
            >
                {displayName}
            </span>
        </div>
    );
}

function MatchCard({ match, teamA, teamB, myIdSet, ownersByTeamId, onTap }) {
    const aMine = myIdSet.has(teamA.id);
    const bMine = myIdSet.has(teamB.id);
    const anyMine = aMine || bMine;
    const handleClick = () => onTap?.(match);

    const card = (
        <button
            type="button"
            data-testid={`bracket-match-${match.id}`}
            onClick={handleClick}
            className={`brutal-card-light p-2 text-xs space-y-1 w-full text-left transition-all hover:border-cyber hover:bg-cyber/5 ${anyMine ? "border-cyber/60" : ""}`}
        >
            <TeamLine team={teamA} score={match.score_a} winner={match.winner_id === teamA.id} isMine={aMine} />
            <TeamLine team={teamB} score={match.score_b} winner={match.winner_id === teamB.id} isMine={bMine} />
        </button>
    );

    // Desktop: hover-card; Mobile: the click handler opens the drawer (HoverCard ignores touch)
    return (
        <HoverCard openDelay={120}>
            <HoverCardTrigger asChild>{card}</HoverCardTrigger>
            <HoverCardContent
                side="top"
                align="center"
                className="bg-ink border-2 border-cyber shadow-brutalYellow w-72 p-4 text-white"
                data-testid={`bracket-popover-${match.id}`}
            >
                <MatchDetailBody match={match} teamA={teamA} teamB={teamB} myIdSet={myIdSet} ownersByTeamId={ownersByTeamId} compact />
            </HoverCardContent>
        </HoverCard>
    );
}

function MatchDetailBody({ match, teamA, teamB, myIdSet, ownersByTeamId, compact = false }) {
    const kickoff = formatKickoff(match.kickoff_at) || formatKickoff(match.scheduled_at);
    // Hoisted status derivation — replaces a 3-deep nested ternary.
    let status;
    if (match.status === "live") status = "LIVE";
    else if (match.status === "completed" || match.winner_id) status = "FINAL";
    else if (kickoff) status = "SCHEDULED";
    else status = "TBD";
    const ownersA = ownersByTeamId[teamA.id] || [];
    const ownersB = ownersByTeamId[teamB.id] || [];
    const padding = compact ? "py-1.5" : "py-2";

    return (
        <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5 text-zinc-500">
                    {status === "LIVE" && <Radio className="w-3.5 h-3.5 text-blaze animate-pulse" />}
                    {status === "SCHEDULED" && <Calendar className="w-3.5 h-3.5" />}
                    <span className={`uppercase tracking-widest font-bold ${status === "LIVE" ? "text-blaze" : "text-zinc-500"}`}>
                        {status}
                    </span>
                </div>
                {kickoff && <span className="text-zinc-400 text-xs">{kickoff}</span>}
            </div>

            <div className="space-y-1">
                <TeamRow team={teamA} score={match.score_a} winner={match.winner_id === teamA.id} isMine={myIdSet.has(teamA.id)} owners={ownersA} padding={padding} />
                <TeamRow team={teamB} score={match.score_b} winner={match.winner_id === teamB.id} isMine={myIdSet.has(teamB.id)} owners={ownersB} padding={padding} />
            </div>

            {(ownersA.length + ownersB.length) > 0 && (
                <div className="brutal-card-light p-2.5 text-[11px] space-y-1" data-testid="bracket-detail-picks">
                    <div className="flex items-center gap-1.5 text-zinc-500 uppercase tracking-widest font-bold">
                        <Users className="w-3 h-3" /> Drafted by
                    </div>
                    {ownersA.length > 0 && (
                        <div className="flex gap-2 items-start">
                            <span className="flex-shrink-0 text-zinc-500">{teamA.flag || "🏳️"}</span>
                            <span className="text-zinc-300">{ownersA.join(", ")}</span>
                        </div>
                    )}
                    {ownersB.length > 0 && (
                        <div className="flex gap-2 items-start">
                            <span className="flex-shrink-0 text-zinc-500">{teamB.flag || "🏳️"}</span>
                            <span className="text-zinc-300">{ownersB.join(", ")}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function TeamRow({ team, score, winner, isMine, padding }) {
    return (
        <div
            data-testid={`bracket-detail-team-${team.id}`}
            className={`flex items-center justify-between gap-2 px-2 ${padding} rounded ${winner ? "bg-cyber/20 border border-cyber" : "border border-[#222]"} ${isMine && !winner ? "border-cyber/60" : ""}`}
        >
            <span className="flex items-center gap-2 min-w-0">
                <span>{team.flag || "🏳️"}</span>
                <span className={`text-sm truncate ${winner ? "font-bold" : ""} ${isMine ? "text-cyber font-semibold" : winner ? "" : "text-zinc-300"}`}>
                    {team.name}
                </span>
                {isMine && <span className="pill bg-cyber text-black text-[9px] py-0 px-1.5">you</span>}
            </span>
            <span className="font-mono font-bold text-sm tabular-nums">{score ?? "-"}</span>
        </div>
    );
}

function TeamLine({ team, score, winner, isMine }) {
    return (
        <div
            data-testid={`bracket-team-row-${team.id}`}
            className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded ${
                winner ? "bg-cyber/20 border border-cyber" :
                isMine ? "border border-cyber/50" :
                "border border-transparent"
            }`}
        >
            <span className="flex items-center gap-1.5 min-w-0">
                <span>{team.flag || "🏳️"}</span>
                <span className={`text-xs truncate ${winner ? "font-bold" : ""} ${isMine ? "text-cyber font-semibold" : winner ? "" : "text-zinc-400"}`}>
                    {team.name}
                </span>
            </span>
            <span className="font-mono font-bold text-xs">{score ?? "-"}</span>
        </div>
    );
}
